import EnvironmentErrorHandler from "Environment/ErrorHandler"
import { polyfill } from "Environment/polyfill"
import { reactRetryLazy } from "Environment/retryLoader"
import { createElement, StrictMode, Suspense } from "react"
import { createRoot } from "react-dom/client"

const SiteContactManager = reactRetryLazy(
  () => import(/* webpackMode: "eager" */ "SiteContactManager")
)
const SrgSiteManager = reactRetryLazy(
  () => import(/* webpackMode: "eager" */ "SrgSiteManager")
)
const SiteSurveyDemographics = reactRetryLazy(
  () => import(/* webpackMode: "eager" */ "SiteSurveyDemographics")
)
const HealthSystemSiteRequest = reactRetryLazy(
  () => import(/* webpackMode: "eager" */ "HealthSystemSiteRequest")
)
const SqcChartDashboard = reactRetryLazy(
  () => import(/* webpackChunkName: "SqcCharts" */ "SqcChartDashboard")
)
const HealthSystemCrm = reactRetryLazy(() => import(/* webpackChunkName: "HCS" */ "HcsCrm"))
const SrmSiteReportApp = reactRetryLazy(
  () => import(/* webpackChunkName: "SrmSiteReportApp" */ "Srm/SiteReport/App")
)
const SrmSiteSearch = reactRetryLazy(
  () => import(/* webpackChunkName: "SrmSiteSearch" */ "./Srm/OmniSearch/App")
)
const SrmOnboardingApp = reactRetryLazy(
  () => import(/* webpackChunkName: "SrmSiteReportApp" */ "Srm/Onboarding/App")
)
const RlbApplication = reactRetryLazy(() => import(/* webpackChunkName: "RLB" */ "Rlb/Application"))
const WebAssets = reactRetryLazy(() => import(/* webpackChunkName: "AdminAssets" */ "WebAssets/Application"))
const OrderBooks = reactRetryLazy(() => import(/* webpackChunkName: "OrderBooks" */ "OrderBooks/OrderBooks"))

const ProviderSurvey = reactRetryLazy(() => import(/* webpackChunkName: "ProviderSurvey" */ "ProviderSurvey/Application"))
const ObservationsSurvey = reactRetryLazy(() => import(/* webpackChunkName: "ObservationsSurvey" */ "ObservationsSurvey/Application"))
const SqcActionPlan = reactRetryLazy(() => import(/* webpackChunkName: "SqcActionPlan" */ "SqcActionPlan/Application"))
const SiteActionPlan = reactRetryLazy(() => import(/* webpackChunkName: "SiteActionPlan" */ "SiteActionPlan/Application"))

type Lazyness = Record<string, React.LazyExoticComponent<any>>
const rootComponents: Lazyness = {
  SiteContactManager,
  SrgSiteManager,
  SqcChartDashboard,
  HealthSystemCrm,
  ResourceLibrary: RlbApplication,
  SrmSiteReportApp,
  SrmSiteSearch,
  SrmOnboardingApp,
  SiteSurveyDemographics,
  HealthSystemSiteRequest,
  WebAssets,
  OrderBooks,
  ProviderSurvey,
  ObservationsSurvey,
  SqcActionPlan,
  SiteActionPlan,
}

const Loading: React.FC<{}> = () => {
  return (
    <div className="pgldr">
      <div aria-busy="true" aria-live="polite" className="pgldr-inner">
        Loading …
      </div>
    </div>
  )
}

const renderSimpleError = (root: HTMLElement) => {
  root.innerHTML =
    "<div style='margin: 30px auto; max-width: 400px; font-size: 16px'>We're sorry but something went wrong. We've been notified about this issue and we'll take a look at it shortly.</div>"
}
window.renderRorComponent = (root, compName, props) => {
  if (rootComponents[compName]) {
    polyfill()
      .then(() => {
        createRoot(root).render(
          <StrictMode>
            <EnvironmentErrorHandler inline={compName !== 'SrmSiteSearch'}>
              <Suspense fallback={<Loading />}>
                {createElement(rootComponents[compName], props)}
              </Suspense>
            </EnvironmentErrorHandler>
          </StrictMode>
        )
      })
      .catch((e) => {
        renderSimpleError(root)
        throw e
      })
  } else {
    renderSimpleError(root)
    throw new Error(`unknown component type "${compName}"`)
  }
}
